import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getSavedPostsStatus,
  savePost,
  unsavePost,
} from "@/lib/savedPostsApi";

const SavedPostsContext = createContext(null);

function statusKey(orgId, postId) {
  return `${orgId}:${postId}`;
}

export function SavedPostsProvider({ children }) {
  const { accessToken } = useAuth();
  const [savedMap, setSavedMap] = useState(() => new Map());
  const knownKeysRef = useRef(new Set());
  const pendingRef = useRef(new Map()); // orgId -> Set(postId)
  const flushTimers = useRef(new Map());
  const prevTokenRef = useRef(accessToken);

  useEffect(() => {
    if (prevTokenRef.current === accessToken) return;
    prevTokenRef.current = accessToken;
    setSavedMap(new Map());
    knownKeysRef.current = new Set();
    pendingRef.current = new Map();
    for (const timer of flushTimers.current.values()) {
      clearTimeout(timer);
    }
    flushTimers.current = new Map();
  }, [accessToken]);

  useEffect(() => {
    return () => {
      for (const timer of flushTimers.current.values()) {
        clearTimeout(timer);
      }
      flushTimers.current = new Map();
    };
  }, []);

  const flushOrg = useCallback(
    async (orgId) => {
      if (!accessToken || !orgId) return;
      const pending = pendingRef.current.get(orgId);
      if (!pending?.size) return;
      const ids = [...pending];
      pending.clear();
      try {
        const { saved } = await getSavedPostsStatus(accessToken, orgId, ids);
        setSavedMap((prev) => {
          const next = new Map(prev);
          for (const id of ids) {
            const key = statusKey(orgId, id);
            knownKeysRef.current.add(key);
            next.set(key, Boolean(saved?.[String(id)]));
          }
          return next;
        });
      } catch (err) {
        console.error("Failed to load saved post status:", err);
        // Allow retry on next ensureStatus
        for (const id of ids) {
          knownKeysRef.current.delete(statusKey(orgId, id));
        }
      }
    },
    [accessToken]
  );

  const ensureStatus = useCallback(
    (orgId, postId) => {
      if (!accessToken || !orgId || postId == null) return;
      const key = statusKey(orgId, postId);
      if (knownKeysRef.current.has(key)) return;
      knownKeysRef.current.add(key);
      if (!pendingRef.current.has(orgId)) {
        pendingRef.current.set(orgId, new Set());
      }
      pendingRef.current.get(orgId).add(String(postId));
      if (flushTimers.current.has(orgId)) return;
      flushTimers.current.set(
        orgId,
        setTimeout(() => {
          flushTimers.current.delete(orgId);
          flushOrg(orgId);
        }, 40)
      );
    },
    [accessToken, flushOrg]
  );

  const isSaved = useCallback(
    (orgId, postId) => {
      if (!orgId || postId == null) return false;
      return Boolean(savedMap.get(statusKey(orgId, postId)));
    },
    [savedMap]
  );

  const setSavedLocal = useCallback((orgId, postId, value) => {
    const key = statusKey(orgId, postId);
    knownKeysRef.current.add(key);
    setSavedMap((prev) => {
      const next = new Map(prev);
      next.set(key, Boolean(value));
      return next;
    });
  }, []);

  const toggleSaved = useCallback(
    async (orgId, postId) => {
      if (!accessToken || !orgId || postId == null) {
        throw new Error("Missing auth or post context");
      }
      const currentlySaved = isSaved(orgId, postId);
      setSavedLocal(orgId, postId, !currentlySaved);
      try {
        if (currentlySaved) {
          await unsavePost(accessToken, orgId, postId);
          setSavedLocal(orgId, postId, false);
          return false;
        }
        await savePost(accessToken, orgId, postId);
        setSavedLocal(orgId, postId, true);
        return true;
      } catch (err) {
        setSavedLocal(orgId, postId, currentlySaved);
        throw err;
      }
    },
    [accessToken, isSaved, setSavedLocal]
  );

  const value = useMemo(
    () => ({
      ensureStatus,
      isSaved,
      toggleSaved,
      setSavedLocal,
    }),
    [ensureStatus, isSaved, toggleSaved, setSavedLocal]
  );

  return (
    <SavedPostsContext.Provider value={value}>
      {children}
    </SavedPostsContext.Provider>
  );
}

export function useSavedPosts() {
  const ctx = useContext(SavedPostsContext);
  if (!ctx) {
    return {
      ensureStatus: () => {},
      isSaved: () => false,
      toggleSaved: async () => false,
      setSavedLocal: () => {},
    };
  }
  return ctx;
}
